// Canonical Spirit Ink print geometry.
//
// One calculation drives the Magic Preview editor, the exact product preview,
// placement serialisation, session restore, ATTACH_PRODUCT, the School Store
// and retail flows, and the production renderer. The identical file is served
// to the browser as theme-update/assets/magic-preview-geometry.v2.js; the
// print-geometry test asserts the two copies stay byte-identical.
//
// Two placement models exist and both are permanent:
//
//   PLACEMENT_GEOMETRY_V1 ("legacy-box-v1")
//     scale sizes an intermediate box inside the print area, the artwork is
//     aspect-fitted and centred inside that box, and offset moves the box.
//     This is exactly calculateProductionPlacement() and it is the only
//     interpretation ever applied to a placement stored before v2 existed.
//
//   PLACEMENT_GEOMETRY_V2 ("supplier-visible-v2")
//     scale sizes the artwork itself against the supplier-safe print area and
//     offset moves the artwork, so scale 1 is the true supplier maximum fit
//     and offset -1/+1 puts the artwork edge on the print-area edge.
//
// Both models agree exactly at offset 0/0 for every scale, which is what keeps
// centred historical placements byte-stable.
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.SpiritInkPrintGeometry = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  const PLACEMENT_GEOMETRY_V1 = 1;
  const PLACEMENT_GEOMETRY_V2 = 2;
  const MODEL_V1 = "legacy-box-v1";
  const MODEL_V2 = "supplier-visible-v2";
  const MIN_SCALE = 0.55;
  const MAX_SCALE = 1;
  const MM_PER_INCH = 25.4;

  const finite = (value) => typeof value === "number" && Number.isFinite(value);

  function invalid() {
    throw new Error("invalid_print_geometry");
  }

  function unsupported() {
    throw new Error("unsupported_placement_geometry");
  }

  // Accepts the stored contract number, the model name, null or undefined for
  // a placement recorded before the field existed, and the numeric strings
  // that Shopify line-item properties and URL parameters produce.
  function normalizeGeometryVersion(value) {
    if (value === undefined || value === null || value === "") return PLACEMENT_GEOMETRY_V1;
    if (value === PLACEMENT_GEOMETRY_V1 || value === "1" || value === MODEL_V1) return PLACEMENT_GEOMETRY_V1;
    if (value === PLACEMENT_GEOMETRY_V2 || value === "2" || value === MODEL_V2) return PLACEMENT_GEOMETRY_V2;
    return unsupported();
  }

  function modelName(value) {
    return normalizeGeometryVersion(value) === PLACEMENT_GEOMETRY_V2 ? MODEL_V2 : MODEL_V1;
  }

  function readPlacement(placement) {
    if (!placement || typeof placement !== "object") invalid();
    const scale = placement.scale;
    const offsetX = placement.offset_x !== undefined ? placement.offset_x : placement.offsetX;
    const offsetY = placement.offset_y !== undefined ? placement.offset_y : placement.offsetY;
    if (![scale, offsetX, offsetY].every(finite)) invalid();
    if (scale < MIN_SCALE || scale > MAX_SCALE) invalid();
    if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) invalid();
    return { scale, offsetX, offsetY };
  }

  function readArea(area) {
    if (!area || typeof area !== "object") invalid();
    const width = area.width;
    const height = area.height;
    const x = area.x === undefined ? 0 : area.x;
    const y = area.y === undefined ? 0 : area.y;
    if (![width, height, x, y].every(finite) || width <= 0 || height <= 0) invalid();
    return { x, y, width, height };
  }

  // The single placement calculation. `area` is any rectangle carrying the
  // print area (millimetres for physical output, pixels for a preview) and
  // `ratio` is the aspect ratio of the artwork's meaningful content.
  function fit(area, ratio, placement, geometryVersion) {
    const box = readArea(area);
    const placed = readPlacement(placement);
    if (!finite(ratio) || ratio <= 0) invalid();
    const version = normalizeGeometryVersion(geometryVersion);

    const boxWidth = box.width * placed.scale;
    const boxHeight = box.height * placed.scale;
    // The limiting dimension is assigned exactly rather than re-derived by
    // division. Dividing reintroduces rounding that can leave the artwork a
    // few ulps outside the print area, which the production geometry guard
    // correctly refuses.
    let width;
    let height;
    if (ratio >= boxWidth / boxHeight) {
      width = boxWidth;
      height = boxWidth / ratio;
    } else {
      height = boxHeight;
      width = boxHeight * ratio;
    }

    if (version === PLACEMENT_GEOMETRY_V1) {
      // Offset moves the intermediate box; the artwork is centred inside it.
      const boxLeft = (box.width - boxWidth) * ((placed.offsetX + 1) / 2);
      const boxTop = (box.height - boxHeight) * ((placed.offsetY + 1) / 2);
      return Object.freeze({
        x: box.x + boxLeft + (boxWidth - width) / 2,
        y: box.y + boxTop + (boxHeight - height) / 2,
        width,
        height,
      });
    }

    // Offset moves the artwork itself inside the supplier-safe print area.
    return Object.freeze({
      x: box.x + (box.width - width) * ((placed.offsetX + 1) / 2),
      y: box.y + (box.height - height) * ((placed.offsetY + 1) / 2),
      width,
      height,
    });
  }

  // Maps a rectangle expressed in the region's physical millimetres onto the
  // region's product-preview pixels. This mapping is what makes the editor
  // show the real supplier area at the right proportion on the garment photo.
  function toPixels(rect, region) {
    const source = readArea(rect);
    const pixels = readArea(region && region.pixels);
    const physical = readArea(region && region.physical);
    const scaleX = pixels.width / physical.width;
    const scaleY = pixels.height / physical.height;
    return Object.freeze({
      x: pixels.x + (source.x - physical.x) * scaleX,
      y: pixels.y + (source.y - physical.y) * scaleY,
      width: source.width * scaleX,
      height: source.height * scaleY,
    });
  }

  // Width, in millimetres, that the artwork occupies at scale 1.
  function fullWidthMm(region, ratio) {
    return fit(region.physical, ratio, { scale: 1, offset_x: 0, offset_y: 0 }, PLACEMENT_GEOMETRY_V2).width;
  }

  function readArtwork(artwork) {
    if (!artwork || typeof artwork !== "object") invalid();
    const ratio = artwork.ratio;
    // Vector artwork is rasterised at output resolution, so it needs no ratio
    // to answer a resolution question.
    if (artwork.kind === "vector") return { kind: "vector", ratio };
    if (!finite(ratio) || ratio <= 0) invalid();
    if (artwork.kind !== "raster") invalid();
    // Raster sizing uses the meaningful content box, never the whole canvas.
    const width = artwork.content_width_px !== undefined ? artwork.content_width_px : artwork.width;
    const height = artwork.content_height_px !== undefined ? artwork.content_height_px : artwork.height;
    if (![width, height].every(finite) || width <= 0 || height <= 0) invalid();
    return { kind: "raster", ratio, width, height };
  }

  function scaleForPpi(region, artwork, ppi) {
    const source = readArtwork(artwork);
    if (source.kind === "vector") return MAX_SCALE;
    if (!finite(ppi) || ppi <= 0) invalid();
    const fullWidth = fullWidthMm(region, source.ratio);
    const fullHeight = fullWidth / source.ratio;
    return Math.min(
      (source.width * MM_PER_INCH) / (ppi * fullWidth),
      (source.height * MM_PER_INCH) / (ppi * fullHeight),
    );
  }

  // Hard ceiling. A raster is never enlarged past the supplier minimum quality
  // floor; vector artwork is rasterised at output resolution and is never
  // constrained by a source pixel count.
  function maxScale(region, artwork) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scaleForPpi(region, artwork, region.raster.minimum_ppi)));
  }

  // The initial fit for a NEW placement: the largest supplier-safe size that
  // still holds the recommended print resolution, never above the hard
  // ceiling. The user remains free to shrink it down to MIN_SCALE.
  function initialScale(region, artwork) {
    const recommended = scaleForPpi(region, artwork, region.raster.recommended_ppi);
    return Math.min(maxScale(region, artwork), Math.max(MIN_SCALE, Math.min(MAX_SCALE, recommended)));
  }

  // Realised resolution of a raster at the selected physical size.
  function effectivePpi(region, artwork, scale) {
    const source = readArtwork(artwork);
    if (source.kind === "vector") return null;
    const rect = fit(region.physical, source.ratio, { scale, offset_x: 0, offset_y: 0 }, PLACEMENT_GEOMETRY_V2);
    return Math.min(
      (source.width * MM_PER_INCH) / rect.width,
      (source.height * MM_PER_INCH) / rect.height,
    );
  }

  function meetsMinimumPpi(region, artwork, scale) {
    const ppi = effectivePpi(region, artwork, scale);
    return ppi === null || ppi >= region.raster.minimum_ppi;
  }

  return Object.freeze({
    PLACEMENT_GEOMETRY_V1,
    PLACEMENT_GEOMETRY_V2,
    MODEL_V1,
    MODEL_V2,
    MIN_SCALE,
    MAX_SCALE,
    MM_PER_INCH,
    normalizeGeometryVersion,
    modelName,
    fit,
    toPixels,
    fullWidthMm,
    maxScale,
    initialScale,
    effectivePpi,
    meetsMinimumPpi,
  });
});
